import { type SubmitEvent, useState } from 'react';
import { Link } from 'react-router';
import axios from 'axios';
import { AlertCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { config } from '@/config';
import { AuthShell } from './AuthShell';

interface ForgotRequest {
  email: string;
}

export default function ForgotPassword() {
  const [formData, setFormData] = useState<ForgotRequest>({ email: '' });
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState(false);

  const onSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setForgotError(false);
    void axios
      .create()
      .post(`${config.backendApi}/api/auth/forgot`, formData, { withCredentials: true })
      .then(
        () => { setForgotSuccess(true); },
        () => { setForgotError(true); },
      );
  };

  if (forgotSuccess) {
    return (
      <AuthShell
        title="Check your email"
        subtitle="If an account exists with that address, a reset link is on its way."
        footer={<Link to="/signin" className="text-primary hover:underline font-medium">Back to sign in</Link>}
      >
        <div className="p-6 flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-brand-soft flex items-center justify-center text-brand mb-2">
            <Mail className="w-5 h-5" />
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your email and we'll send you a reset link."
      footer={
        <>
          Remember it after all?{' '}
          <Link to="/signin" className="text-primary hover:underline font-medium">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="p-6 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="forgot-email">Email</Label>
          <Input
            id="forgot-email"
            type="email"
            autoComplete="email"
            required
            value={formData.email}
            onChange={(e) => { setFormData({ ...formData, email: e.target.value }); }}
          />
        </div>

        {forgotError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              There was a problem resetting your password. Please try again.
            </AlertDescription>
          </Alert>
        ) : null}

        <Button type="submit" className="w-full">
          Send reset link
        </Button>
      </form>
    </AuthShell>
  );
}
